// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getLocation, getQueryStrings } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import AddressInfoDisplay, { AddressInfoDisplayStyles } from "@insite/content-library/Components/AddressInfoDisplay";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker/DatePicker";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import StyledBox, { styledBoxStyle, StyledBoxStyles } from "@keller/App/Components/Common/StyledBox";
import Notify from "@keller/App/Components/Notify";
import { loadAllAccountInquiry } from "@keller/App/Handlers/AccountInquiry/AccountInquiry";
import OrderInformationStyles from "@keller/App/SharedStyle/MyAccount/Details/OrderInformationStyles";
import { getAccountInquiry } from "@keller/App/Store/Selectors/AccountInquirySelectors";
import { xScreen } from "@keller/XDL/Utilities";
import qs from "qs";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => ({
    accountInquiry: getAccountInquiry(state),
    location: getLocation(state),
    initialAsOfDate: getQueryStrings(state)?.asOfDate || undefined,
    sessionShipTo: getShipToState(state, state.context.session.shipToId).value,
    sessionBillTo: getBillToState(state, state.context.session.billToId).value,
});

const mapDispatchToProps = {
    loadAllAccountInquiry,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface AccountInquiryStyles {
    centerWrapper?: InjectableCss;
    container?: InjectableCss;
    titleWrapper?: InjectableCss;
    datePicker?: DatePickerPresentationProps;
    styledBox?: StyledBoxStyles;
    addressingContainer?: GridContainerProps;
    sessionShippingInformationGridItem?: GridItemProps;
    sessionShippingAddressTitle?: TypographyPresentationProps;
    sessionShippingAddress?: AddressInfoDisplayStyles;
    sessionBillingInformationGridItem?: GridItemProps;
    sessionBillingAddressTitle?: TypographyPresentationProps;
    sessionBillingAddress?: AddressInfoDisplayStyles;
    item?: { container?: InjectableCss; label?: TypographyPresentationProps; value?: TypographyPresentationProps };
}

const accountInquiryStyles: AccountInquiryStyles = {
    centerWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
        `,
    },
    container: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: space-between;
            gap: 0 10px;
        `,
    },
    titleWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            flex-basis: 100%;
            margin-bottom: 24px;
            & > div {
                width: 100%;
            }
        `,
    },
    datePicker: {
        cssOverrides: {
            formField: css`
                ${xScreen("gt", "xs")} {
                    max-width: 350px;
                }
                ${xScreen("gt", "sm")} {
                    max-width: 308px;
                }
            `,
        },
    },
    styledBox: {
        container: {
            css: css`
                display: flex;
                flex-direction: column;
                margin-bottom: 20px;
                width: 100%;
                flex: 1 1 0;
                min-width: 320px;
                max-width: 418px;

                ${xScreen("lte", "sm")} {
                    max-width: 350px;
                }

                ${xScreen("lte", "xs")} {
                    max-width: 100%;
                    min-width: initial;
                    flex-basis: 100%;
                }

                & > div:not(:last-child) {
                    margin-bottom: 16px;
                }
            `,
        },
    },
    item: {
        container: {
            css: css`
                display: flex;
                justify-content: space-between;
            `,
        },
        label: {
            size: 15,
            weight: 900,
            transform: "capitalize",
        },
        value: {
            size: 15,
            weight: "normal",
        },
    },
    addressingContainer: {
        gap: 46,
        css: css`
            margin-bottom: 40px;
        `,
    },
    sessionBillingInformationGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    sessionShippingInformationGridItem: {
        width: [12, 6, 6, 6, 6],
        css: css`
            flex-direction: column;
        `,
    },
    sessionShippingAddressTitle: {
        ...OrderInformationStyles.address?.heading,
    },
    sessionShippingAddress: {
        wrapper: {
            css: css`
                width: 100%;
                max-width: 500px;
                height: 100%;
                ${styledBoxStyle}
            `,
        },
    },
    sessionBillingAddressTitle: {
        ...OrderInformationStyles.address?.heading,
    },
    sessionBillingAddress: {
        wrapper: {
            css: css`
                width: 100%;
                max-width: 500px;
                height: 100%;
                ${styledBoxStyle}
            `,
        },
    },
};

const styles = accountInquiryStyles;

const ItemElement = ({ label, value }: { label: string; value?: string }) => {
    return (
        <StyledWrapper {...styles.item?.container}>
            <Typography {...styles.item?.label}>{label}</Typography>
            <Typography {...styles.item?.value}>{value || "--"}</Typography>
        </StyledWrapper>
    );
};

const AccountInquiry: FC<Props> = ({
    initialAsOfDate,
    sessionShipTo,
    sessionBillTo,
    location,
    loadAllAccountInquiry,
    accountInquiry,
}) => {
    const [selectedAsOfDate, setSelectedAsOfDate] = useState(initialAsOfDate ? new Date(initialAsOfDate) : new Date());
    const history = useHistory();

    useEffect(() => {
        const queryString = qs.stringify({ asOfDate: convertDateToDateOnlyString(selectedAsOfDate) });
        history.replace(`${location.pathname}${queryString !== "" ? `?${queryString}` : ""}`);

        loadAllAccountInquiry({
            asOfDate: convertDateToDateOnlyString(selectedAsOfDate),
        });
    }, [selectedAsOfDate]);

    const handleAsOfDateDateChanged = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        if (
            !selectedDay ||
            convertDateToDateOnlyString(selectedDay) === convertDateToDateOnlyString(selectedAsOfDate)
        ) {
            return;
        }

        setSelectedAsOfDate(selectedDay);
    };

    if (accountInquiry.isLoading) {
        return (
            <StyledWrapper {...styles.centerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    if (accountInquiry.failedMessage) {
        return (
            <StyledWrapper {...styles.container}>
                <Notify type="danger">{accountInquiry.failedMessage}</Notify>
            </StyledWrapper>
        );
    }

    const data = accountInquiry.dataViews;
    return (
        <StyledWrapper {...styles.container}>
            <GridContainer {...styles.addressingContainer}>
                {sessionBillTo && (
                    <GridItem {...styles.sessionBillingInformationGridItem}>
                        <Typography {...styles.sessionBillingAddressTitle} as="h3">
                            {translate("Billing Address")}
                        </Typography>
                        <AddressInfoDisplay
                            companyName={sessionBillTo.companyName}
                            address1={sessionBillTo.address1}
                            address2={sessionBillTo.address2}
                            address3={sessionBillTo.address3}
                            address4={sessionBillTo.address4}
                            city={sessionBillTo.city}
                            postalCode={sessionBillTo.postalCode}
                            email={sessionBillTo.email}
                            phone={sessionBillTo.phone}
                            firstName={sessionBillTo.firstName}
                            lastName={sessionBillTo.lastName}
                            country={sessionBillTo.country?.abbreviation}
                            attention={sessionBillTo.attention}
                            state={sessionBillTo.state ? sessionBillTo.state.abbreviation : undefined}
                            extendedStyles={styles.sessionBillingAddress}
                        />
                    </GridItem>
                )}
                {sessionShipTo && (
                    <GridItem {...styles.sessionShippingInformationGridItem}>
                        <Typography {...styles.sessionShippingAddressTitle} as="h3">
                            {translate("Shipping Address")}
                        </Typography>
                        <AddressInfoDisplay
                            companyName={sessionShipTo.companyName}
                            address1={sessionShipTo.address1}
                            address2={sessionShipTo.address2}
                            address3={sessionShipTo.address3}
                            address4={sessionShipTo.address4}
                            city={sessionShipTo.city}
                            postalCode={sessionShipTo.postalCode}
                            email={sessionShipTo.email}
                            phone={sessionShipTo.phone}
                            firstName={sessionShipTo.firstName}
                            lastName={sessionShipTo.lastName}
                            country={sessionShipTo.country?.abbreviation}
                            attention={sessionShipTo.attention}
                            state={sessionShipTo.state ? sessionShipTo.state.abbreviation : undefined}
                            extendedStyles={styles.sessionShippingAddress}
                        />
                    </GridItem>
                )}
            </GridContainer>
            <StyledWrapper {...styles.titleWrapper}>
                <DatePicker
                    label={translate("As of Date")}
                    selectedDay={new Date(selectedAsOfDate)}
                    onDayChange={handleAsOfDateDateChanged}
                    dateTimePickerProps={{
                        clearIcon: null,
                    }}
                    {...styles.datePicker}
                />
            </StyledWrapper>
            <StyledBox extendedStyles={styles.styledBox}>
                <ItemElement label={translate("future")} value={data.future} />
                <ItemElement label={translate("current")} value={data.current} />
                <ItemElement label={translate("31-60")} value={data.thirty} />
                <ItemElement label={translate("61-90")} value={data.sixty} />
                <ItemElement label={translate("91-120")} value={data.ninety} />
                <ItemElement label={translate("over 120")} value={data.oneTwenty} />
                <ItemElement label={translate("total")} value={data.arTotal} />
            </StyledBox>
            <StyledBox extendedStyles={styles.styledBox}>
                <ItemElement label={translate("deposits")} value={data.arDeposits} />
                <ItemElement label={translate("orders")} value={data.arOrders} />
                <ItemElement label={translate("MTD sales")} value={data.mtdSales} />
                <ItemElement label={translate("YTD sales")} value={data.ytdSales} />
                <ItemElement label={translate("6 month avg.")} value={data.sixMonthAverage} />
                <ItemElement label={translate("6 month high")} value={data.sixMonthHigh} />
                <ItemElement label={translate("payment days")} value={data.paymentDays} />
            </StyledBox>
            <StyledBox extendedStyles={styles.styledBox}>
                <ItemElement label={translate("terms")} value={data.arTerms} />
                <ItemElement label={translate("credit limit")} value={data.arCreditLimit} />
                <ItemElement label={translate("credit available")} value={data.arCreditAvail} />
                <ItemElement label={translate("last sale amount")} value={data.lastSale?.amount} />
                <ItemElement label={translate("last sale date")} value={data.lastSale?.date} />
                <ItemElement label={translate("last payment amount")} value={data.lastPayment?.amount} />
                <ItemElement label={translate("last sale date")} value={data.lastPayment?.date} />
            </StyledBox>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AccountInquiry),
    definition: {
        group: "Xngage Custom" as any,
        displayName: "Account Inquiry",
        fieldDefinitions: [],
    },
};

export default widgetModule;
